import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  Button as MuiButton,
} from "@mui/material";
import * as React from "react";
import { Account } from "../../types/Account";
import { Attachment } from "../../types/Attachment";
import { Case } from "../../types/Case";
import { AdditionalHelpDetails, YesNo } from "@/types/index";
import { Button } from "../../components/atoms/Button";
import { YesNoCheckboxField } from "../../components/atoms/YesNoField";
import { Accordion } from "../../components/molecules/Accordion";
import { updateAdditionalServices } from "@/services/api/caseApi";
import { DashboardInfoContext } from "@/store/DashboardProvider";
import { servicesFromCase } from "@/helpers/additional-services-helpers";
import { FormField } from "@/components/molecules";
import DoneIcon from "@mui/icons-material/Done";
import InheritNow from "@/assets/images/inheritnow.svg";
import { colors } from "../../styles/constants";
import { navigate } from "@storybook/addon-links";

const INTERNAL_FIELD = "requestInheritanceFundingFinance";
export const InheritNowAccordion: React.FC = () => {
  const {
    state: { case: caseRecord, caseId, signature, showInheritNowAccordion },
    dispatch,
  } = React.useContext(DashboardInfoContext);
  const [show, setShow] = React.useState(false);
  const showDialog = React.useCallback(() => setShow(true), [setShow]);
  const hideDialog = React.useCallback(() => {
    dispatch({ type: "REFRESH" });
    setShow(false);
  }, [setShow]);

  const record = { [INTERNAL_FIELD]: YesNo.Yes } as AdditionalHelpDetails;
  const onSubmit = React.useCallback(() => {
    updateAdditionalServices({
      caseId,
      signature,
      record,
    }).then(
      () => {
        showDialog();
      },
      (error: any) => {
        console.error(error);
        showDialog();
      }
    );
  }, [caseId, signature, record, dispatch, showDialog]);

  if (!showInheritNowAccordion) return null;
  if (!caseRecord) return null;
  return (
    <>
      <Accordion
        expanded={true}
        title="The probate process can take a long time. Why Wait? Receive your inheritance faster!"
        titleColor={colors.inheritNowBlue}
      >
        <Stack rowGap={1} sx={{ width: "100%" }}>
          <Grid container sx={{ width: "100%" }}>
            <Box sx={{ gap: 2, width: "100%" }}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  marginLeft: 2,
                  boxShadow: "0 4px 24px rgba(0, 66, 140, 0.08)",
                  borderWidth: "1px",
                  borderColor: colors.inheritNowBlue,
                  marginRight: 2,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="h6">
                    INHERITANCE LOANS &nbsp; | &nbsp; INHERITANCE TAX (IHT) SOLUTIONS
                  </Typography>
                  <br />
                  <Typography>
                    Cover HMRC tax bills immediately, avoiding delays in probate, or release part of
                    your inheritance now, before probate is completed.{" "}
                  </Typography>
                  <div style={{ marginBottom: 6 }}></div>
                  <List dense disablePadding sx={{ mb: 2 }}>
                    <ListItem disableGutters sx={{ alignItems: "center" }}>
                      <ListItemIcon sx={{ minWidth: 28, pt: "2px" }}>
                        <DoneIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{ variant: "body1" }}
                        primary="     Fast decisions and funding in as little as 24 hours."
                      />
                    </ListItem>
                    <ListItem disableGutters sx={{ alignItems: "center" }}>
                      <ListItemIcon sx={{ minWidth: 28, pt: "2px" }}>
                        <DoneIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{ variant: "body1" }}
                        primary="     No personal liability - funds are repaid directly and exclusively from the estate."
                      />
                    </ListItem>
                    <ListItem disableGutters sx={{ alignItems: "center" }}>
                      <ListItemIcon sx={{ minWidth: 28, pt: "2px" }}>
                        <DoneIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{ variant: "body1" }}
                        primary="     Highly competitive interest rates, capped at 18 months."
                      />
                    </ListItem>
                    <ListItem disableGutters sx={{ alignItems: "center" }}>
                      <ListItemIcon sx={{ minWidth: 28, pt: "2px" }}>
                        <DoneIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{ variant: "body1" }}
                        primary="No repayments until the estate has settled"
                      />
                    </ListItem>

                    <ListItem disableGutters sx={{ alignItems: "center" }}>
                      <ListItemIcon sx={{ minWidth: 28, pt: "2px" }}>
                        <DoneIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{ variant: "body1" }}
                        primary="Free, no-obligation quotes."
                      />
                    </ListItem>
                  </List>
                  <Divider sx={{ my: 1.5, backgroundColor: colors.inheritNowBlue }} />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Typography variant="body2" color="text.secondary">
                      Provided by
                    </Typography>
                    <Box
                      component="img"
                      src={InheritNow}
                      alt="InheritNOW UK"
                      sx={{ height: 22, cursor: "pointer" }}
                      onClick={() => window.open("https://inheritnow.co.uk/", "_blank")}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>

          <Box>
            <br />
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button variant="inheritNowBlue" size="medium" onClick={onSubmit}>
                Register Interest
              </Button>
              <MuiButton
                variant="outlined"
                size="medium"
                sx={{
                  backgroundColor: "white !important",
                  borderColor: `${colors.inheritNowBlue} !important`,
                  color: `${colors.inheritNowBlue} !important`,
                  textTransform: "uppercase",
                  borderRadius: "30px",
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "16px",
                  padding: "8px 32px",
                  "&:hover": {
                    backgroundColor: `${colors.inheritNowBlue} !important`,
                    color: "white !important",
                  },
                }}
                onClick={() => window.open("https://inheritnow.co.uk/", "_blank")}
              >
                Learn More
              </MuiButton>
            </Box>
          </Box>
        </Stack>
      </Accordion>{" "}
      <Dialog open={show} onClose={hideDialog}>
        {" "}
        <DialogTitle>Selected partners updated</DialogTitle>
        <DialogContent>
          <p>Our team will be in touch with you shortly to discuss your additional services.</p>
        </DialogContent>
        <DialogActions>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              columnGap: "40px",
              alignItems: "baseline",
            }}
          >
            <div>
              <Button variant="inheritNowBlue" size="small" onClick={hideDialog}>
                <span style={{ textTransform: "uppercase" }}>OK</span>
              </Button>
            </div>
          </div>
        </DialogActions>{" "}
      </Dialog>
      <br />
    </>
  );
};
